import { useMsal } from '@azure/msal-react';
import { useState, useMemo, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import bootstrapPlugin from '@fullcalendar/bootstrap';
import { Link } from 'react-router-dom';
import KanbanAPI from '../../api/kanban-board/kanban-api-factory';
import KanbanFunctions from '../../api/kanban-board/kanban-functions';
import { EventModal } from '../../components/calendar/event-modal';
import CalendarApi from '../../api/calendar/calendar-api-factory';
import StandardEventModal from '../../components/calendar/standard-event-modal';
import { KanbanContext } from '../kanban-board/kanban-board';

export const Calendar = () => {

	const defaultState = {
		plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, bootstrapPlugin],
		headerToolbar: {
			left: 'dayGridMonth,timeGridWeek,timeGridDay',
			center: 'title',
			right: 'prev,next today'
		},
		buttonText: {
			today: 'Today',
			month: 'Month',
			week: 'Week',
			day: 'Day'
		},
		views: {
			timeGrid: {
				eventLimit: 6
			}
		}
	}

	const { instance, inProgress, accounts } = useMsal();
	const kanbanAPI = useMemo(() => new KanbanAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
	const calendarApi = useMemo(() => new CalendarApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

	const [events, setEvents] = useState([]);
	const [dateRange, setDateRange] = useState(null);
	const [showKanbanEventModal, setShowKanbanEventModal] = useState(false);
	const [selectedTileId, setSelectedTileId] = useState(null);
	const [selectedProjectId, setSelectedProjectId] = useState(null);
	const [showStandardEventModal, setShowStandardEventModal] = useState(false);
	const [selectedStandardEventId, setSelectedStandardEventId] = useState(null);
	const [selectedStandardEvent, setSelectedStandardEvent] = useState(null);
	const [newDateSelected, setNewDateSelected] = useState(null);
	const [fetchInProgress, setFetchInProgress] = useState(false);
	const [shouldFetch, setShouldFetch] = useState(true);

	useEffect(() => {
		// In test or misconfigured environments the API factories may be
		// stubbed; if the expected methods are missing, skip fetching.
		if (!kanbanAPI || typeof kanbanAPI.getProjectData !== 'function' ||
			!calendarApi || typeof calendarApi.getEvents !== 'function') {
			return;
		}

		const getEvents = async () => {

			const data = await kanbanAPI.getProjectData();
			const items = KanbanFunctions.getDueDatesAsCalendarItems(data) ?? [];

			const from = dateRange && dateRange.from;
			const to = dateRange && dateRange.to;

			const calendarEvents = await calendarApi.getEvents(from, to);

			setEvents([...items, ...(calendarEvents || [])]);
			setFetchInProgress(false);
			setShouldFetch(false);
		};
		if (!fetchInProgress && !!shouldFetch && !!dateRange) {
			setFetchInProgress(true);
			getEvents();
		}

	}, [fetchInProgress, shouldFetch, dateRange, kanbanAPI, calendarApi]);

	const handleDatesSet = (arg) => {
		let from;
		let to;

		if (arg.start instanceof Date && arg.end instanceof Date) {
			// For all views, use the visible date range:
			// from = first visible day at 00:00:00.000 UTC
			// to = last visible day at 23:59:59.999 UTC (end is exclusive)
			const start = arg.start;
			const endExclusive = arg.end;

			from = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 0, 0, 0, 0));
			to = new Date(Date.UTC(endExclusive.getUTCFullYear(), endExclusive.getUTCMonth(), endExclusive.getUTCDate() - 1, 23, 59, 59, 999));
		}
		else {
			from = arg.start;
			to = arg.end;
		}

		setDateRange({ from, to });
		setShouldFetch(true);
	};

	const handleCancelKanbanModal = () => {
		setShowKanbanEventModal(false);
		setSelectedTileId(null);
		setSelectedProjectId(null);
	};

	const handleSaveStandardEvent = async event => {
		setShowStandardEventModal(false);
		setSelectedStandardEventId(null);

		const result = await calendarApi.saveEvent(event);
		const occurences = Array.isArray(result) ? result : [result];

		setEvents(current => {
			if (!occurences || occurences.length === 0) {
				return current;
			}

			const getSeriesKey = e => {
				if (!e) {
					return null;
				}
				const ext = e.extendedProps || {};
				return ext.calendarEventId || e.id;
			};

			const seriesKey = getSeriesKey(occurences[0]);

			// If we have a stable underlying event/series id, remove all
			// existing occurrences for that series; otherwise fall back to
			// replacing by the returned ids.
			let filtered;
			if (seriesKey) {
				filtered = current.filter(e => getSeriesKey(e) !== seriesKey);
			}
			else {
				const ids = new Set(occurences.map(o => o && o.id));
				filtered = current.filter(e => !ids.has(e.id));
			}

			return [...filtered, ...occurences];
		});

	};

	const handleCancelStandardModal = () => {
		setShowStandardEventModal(false);
		setSelectedStandardEventId(null);
		setSelectedStandardEvent(null);
	};

	const handleDeleteEvent = async (event, options = {}) => {
		const mode = options.mode || 'entire-series';

		const getSeriesKey = e => {
			if (!e) {
				return null;
			}
			const ext = e.extendedProps || {};
			return ext.calendarEventId || e.id;
		};

		// Helper to normalise any supported date-like value to a JS Date
		const toDate = value => {
			if (!value) return null;
			if (value instanceof Date) return value;
			if (value._isAMomentObject && typeof value.toDate === 'function') return value.toDate();
			return new Date(value);
		};

		if (mode === 'this-and-future') {
			// For "this and future" deletions on a recurring series, shorten the
			// recurrence end date to the day before the clicked occurrence and
			// save the updated series instead of deleting it outright.
			const start = toDate(event.start);
			if (!start) {
				// If we cannot determine a start date, do not risk deleting
				// the series; just abort this operation.
				console.warn('Calendar: cannot determine event.start for this-and-future delete; aborting.');
			} else {
				const seriesKey = getSeriesKey(event);
				const prevDay = new Date(Date.UTC(
					start.getUTCFullYear(),
					start.getUTCMonth(),
					start.getUTCDate() - 1,
				));
				const yyyy = prevDay.getUTCFullYear();
				const mm = String(prevDay.getUTCMonth() + 1).padStart(2, '0');
				const dd = String(prevDay.getUTCDate()).padStart(2, '0');
				const newRecurrenceEnd = `${yyyy}-${mm}-${dd}`;

				// When shortening a series, never move the original event's
				// start/end. Use the earliest known occurrence in this series as
				// the anchor for the update payload, and only change the
				// recurrence end.
				let anchorEvent = null;
				if (seriesKey && Array.isArray(events)) {
					const seriesEvents = events.filter(e => getSeriesKey(e) === seriesKey);
					const dated = seriesEvents
						.map(e => ({ ev: e, s: toDate(e && e.start) }))
						.filter(x => !!x.s)
						.sort((a, b) => a.s - b.s);
					anchorEvent = dated.length > 0 ? dated[0].ev : null;
				}
				const baseForUpdate = anchorEvent || event;
				const updated = Object.assign({}, baseForUpdate, { recurrenceEnd: newRecurrenceEnd });
				await calendarApi.saveEvent(updated);

				// Do not rely on the API's return shape for trimming; instead,
				// keep all earlier occurrences in the current state and only
				// remove events from this series whose start is on/after the
				// clicked occurrence's start.
				setEvents(current => current.filter(e => {
					const key = getSeriesKey(e);
					if (!seriesKey || key !== seriesKey) {
						return true;
					}
					const s = toDate(e.start);
					if (!s) return true;
					return s < start;
				}));
			}
		} else {
			// Default behaviour: delete the entire series.
			await calendarApi.deleteEvent(event);

			const seriesKey = getSeriesKey(event);
			setEvents(current => current.filter(e => getSeriesKey(e) !== seriesKey));
		}

		setSelectedStandardEvent(null);
		setSelectedStandardEventId(null);
		setShowStandardEventModal(false);
	};

	const handleCancelOccurrence = async (event) => {
		// Call the dedicated cancel-occurrence endpoint to cancel only
		// this specific instance while keeping the rest of the series.
		await calendarApi.cancelOccurrence(event);

		// Optimistically remove just this occurrence from the visible
		// events; the series and other instances remain intact.
		setEvents(current => current.filter(e => e.id !== event.id));

		setSelectedStandardEvent(null);
		setSelectedStandardEventId(null);
		setShowStandardEventModal(false);
	};

	const handleCreateNewEvent = date => {
		setNewDateSelected(date.date);
		setShowStandardEventModal(true);
		setSelectedStandardEventId(-1);
		setSelectedStandardEvent(null);
	};

	return (

		<KanbanContext.Provider value={{
			setData: () => { },
			toggleSubTaskComplete: () => { },
			setSubTaskCheckCompleteStatus: () => { },
			upsertTile: () => { },
			deleteSubTask: () => { },
			addSubTask: () => { },
			updateSubTask: () => { },
			moveColumn: () => { },
			deleteColumn: () => { },
			markTileToDelete: () => { }
		}}>
			{selectedTileId &&
				<EventModal
					show={showKanbanEventModal}
					handleCancel={handleCancelKanbanModal}
					tileId={selectedTileId}
					selectedProjectId={selectedProjectId}
				/>}

			{showStandardEventModal &&
				<StandardEventModal
					show={showStandardEventModal}
					handleSave={e => handleSaveStandardEvent(e)}
					handleCancel={handleCancelStandardModal}
					handleDelete={(event, options) => handleDeleteEvent(event, options)}
					handleCancelOccurrence={event => handleCancelOccurrence(event)}
					standardEventId={selectedStandardEventId}
					newDateSelected={newDateSelected}
					initialEvent={selectedStandardEvent}
				/>}


			<ol className="breadcrumb float-xl-end">
				<li className="breadcrumb-item"><Link to="/calendar">Home</Link></li>
				<li className="breadcrumb-item active">Calendar</li>
			</ol>
			<h1 className="page-header">Calendar</h1>
			<hr />

			<FullCalendar
				plugins={defaultState.plugins}
				initialView="dayGridMonth"
				themeSystem="bootstrap"
				headerToolbar={defaultState.headerToolbar}
				buttonText={defaultState.buttonText}
				datesSet={handleDatesSet}
				dateClick={e => handleCreateNewEvent(e)}
				eventClick={e => {

					if (e.event.extendedProps) {
						if (e.event.extendedProps.eventType) {
							switch (e.event.extendedProps.eventType) {

								case "standard":
								default:
									setShowStandardEventModal(true);
									// Capture a plain object copy of the clicked event
									// so the modal can avoid reloading it from the API.
									const apiEvent = e.event;
									const getSeriesKey = ev => {
										if (!ev) {
											return null;
										}
										const ext = ev.extendedProps || {};
										return ext.calendarEventId || ev.id;
									};

									// Determine whether this is the first occurrence in
									// the series based on the earliest start date.
									const seriesKey = getSeriesKey(apiEvent);
									let isFirstOccurrence = true;
									if (seriesKey && Array.isArray(events)) {
										const seriesEvents = events.filter(ev => getSeriesKey(ev) === seriesKey);
										const toDate = value => {
											if (!value) {
												return null;
											}
											if (value instanceof Date) {
												return value;
											}
											return new Date(value);
										};

										const clickedStart = toDate(apiEvent.start);
										if (clickedStart) {
											for (const ev of seriesEvents) {
												const s = toDate(ev && ev.start);
												if (s && s < clickedStart) {
													isFirstOccurrence = false;
													break;
												}
											}
										}
									}

									const plain = typeof apiEvent.toPlainObject === 'function'
										? apiEvent.toPlainObject()
										: {
											id: apiEvent.id,
											allDay: apiEvent.allDay,
											title: apiEvent.title,
											start: apiEvent.start,
											end: apiEvent.end,
											extendedProps: apiEvent.extendedProps || {}
										};
									plain.isFirstOccurrence = isFirstOccurrence;
									setSelectedStandardEventId(apiEvent.id);
									setSelectedStandardEvent(plain);
									break;

								case "kanban":
									setShowKanbanEventModal(true);
									setSelectedTileId(e.event.id);
									setSelectedProjectId(e.event.extendedProps.project);
									break;
							}
						}
					}
				}}
				events={events}
				views={defaultState.views}
			/>

		</KanbanContext.Provider>




	);

}